// Breath Ambience — a soft, continuous meditation pad synthesized in the
// browser (no audio files, no bandwidth, no permissions).
//
// It layers three sine oscillators tuned in a warm harmonic (root, fifth
// and gentle sub) through a low-pass filter, with two LFOs shaping the
// output:
//   • a slow amplitude LFO that inhales for ~5.5s and exhales for ~5.5s
//     — the sound literally breathes with the member,
//   • a very slow filter LFO that opens and closes the timbre so the pad
//     never feels static or synthetic.
//
// Volume is deliberately extremely low (peak ~0.045) so N8's voice always
// sits comfortably on top; the pad is felt more than heard.

let _ctx = null;
let _instance = null;

function ctx() {
  if (_ctx) return _ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    _ctx = new C();
    return _ctx;
  } catch { return null; }
}

/**
 * Begin the breathing ambience. Idempotent — calling start() while it
 * is already playing simply returns the existing instance.
 * Options:
 *   • peakVolume — cap for the amplitude LFO (default 0.045, very quiet).
 *   • breathSeconds — full inhale-exhale cycle length (default 11s).
 */
export function startBreathAmbience({
  peakVolume = 0.045,
  breathSeconds = 11,
} = {}) {
  if (_instance) return _instance;
  const c = ctx();
  if (!c) return null;
  try {
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;

    // Master gain — always starts at 0 so we can fade the pad in gently
    // when start() is called and out again when stop() is called.
    const master = c.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(peakVolume, now + 2.2);

    // Warm low-pass filter — softens the top end so oscillators don't
    // read as synthy. A slow LFO on cutoff makes it feel alive.
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(680, now);
    filter.Q.setValueAtTime(0.7, now);

    const filterLfo = c.createOscillator();
    const filterLfoGain = c.createGain();
    filterLfo.type = "sine";
    filterLfo.frequency.setValueAtTime(1 / 24, now); // very slow
    filterLfoGain.gain.setValueAtTime(180, now);
    filterLfo.connect(filterLfoGain);
    filterLfoGain.connect(filter.frequency);
    filterLfo.start(now);

    // Three-oscillator drone — root (216Hz, one octave below 432 for
    // grounding warmth), fifth (324Hz) and a very soft sub (108Hz).
    // Kept at low individual gains so their sum doesn't clip.
    const voices = [
      { freq: 216,  type: "sine",     gain: 0.55, detune: -3 },
      { freq: 324,  type: "sine",     gain: 0.35, detune: 4  },
      { freq: 108,  type: "triangle", gain: 0.30, detune: 0  },
    ];

    const voiceNodes = voices.map((v) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = v.type;
      osc.frequency.setValueAtTime(v.freq, now);
      osc.detune.setValueAtTime(v.detune, now);
      g.gain.setValueAtTime(v.gain, now);
      osc.connect(g);
      g.connect(filter);
      osc.start(now);
      return { osc, g };
    });

    // Breath LFO — a sine wave slowly modulating the master gain so the
    // pad rises and falls like a human breath. We route the LFO through
    // an offset so amplitude oscillates around ~70% of peak rather than
    // dropping to silence between breaths.
    const breathLfo = c.createOscillator();
    const breathLfoDepth = c.createGain();
    breathLfo.type = "sine";
    breathLfo.frequency.setValueAtTime(1 / breathSeconds, now);
    breathLfoDepth.gain.setValueAtTime(peakVolume * 0.35, now);
    breathLfo.connect(breathLfoDepth);
    breathLfoDepth.connect(master.gain);
    breathLfo.start(now);

    filter.connect(master);
    master.connect(c.destination);

    _instance = {
      context: c,
      master,
      filter,
      filterLfo,
      filterLfoGain,
      breathLfo,
      breathLfoDepth,
      voiceNodes,
      peakVolume,
    };
    return _instance;
  } catch {
    return null;
  }
}

/**
 * Gently fade the ambience out and free the audio nodes. Safe to call
 * even when nothing is playing.
 */
export function stopBreathAmbience({ fadeSeconds = 1.6 } = {}) {
  const inst = _instance;
  if (!inst) return;
  _instance = null;
  const c = inst.context;
  try {
    const now = c.currentTime;
    // Cancel the breathing LFO's contribution then ramp the master to 0.
    try { inst.breathLfoDepth.gain.setValueAtTime(0, now); } catch { /* noop */ }
    inst.master.gain.cancelScheduledValues(now);
    inst.master.gain.setValueAtTime(Math.max(inst.master.gain.value, 0.0001), now);
    inst.master.gain.linearRampToValueAtTime(0, now + fadeSeconds);

    const stopAt = now + fadeSeconds + 0.1;
    inst.voiceNodes.forEach(({ osc }) => { try { osc.stop(stopAt); } catch { /* noop */ } });
    try { inst.filterLfo.stop(stopAt); } catch { /* noop */ }
    try { inst.breathLfo.stop(stopAt); } catch { /* noop */ }
    setTimeout(() => {
      try { inst.master.disconnect(); } catch { /* noop */ }
      try { inst.filter.disconnect(); } catch { /* noop */ }
    }, (fadeSeconds + 0.3) * 1000);
  } catch { /* silent */ }
}

/**
 * Temporarily lower or raise the ambience volume without stopping it —
 * useful when a pause button is pressed so the pad drops to a whisper
 * rather than disappearing entirely.
 */
export function duckBreathAmbience(level = 0.35, seconds = 0.6) {
  const inst = _instance;
  if (!inst) return;
  try {
    const c = inst.context;
    const now = c.currentTime;
    const target = Math.max(0, Math.min(1, level)) * inst.peakVolume;
    inst.master.gain.cancelScheduledValues(now);
    inst.master.gain.setValueAtTime(Math.max(inst.master.gain.value, 0.0001), now);
    inst.master.gain.linearRampToValueAtTime(target, now + seconds);
  } catch { /* noop */ }
}

/** True while the ambience is currently active. */
export function isBreathAmbiencePlaying() {
  return !!_instance;
}
